import * as React from 'react';
import { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import MaterialSymbol from '../../components/MaterialSymbol';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button, Grid, Divider, Link, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import Copyright from '../../components/Copyright';
import GoogleLoginButton from '../../components/GoogleLoginButton';
import { AuthContext } from '../../context/AuthContext';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, AUTH_URLS } from '../../common/AppConstants';
import { notifyClientErrors } from '../../common/AppUtils';
import * as AppUrls from '../../common/AppUrls';
import { Helmet } from 'react-helmet';
import logger from '../../logger';

export default function AcceptInvite() {
  const { profile_id, invite_token, invite_type } = useParams();
  const { loggedIn, login } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  // const [email, setEmail] = useState('');
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  // const [otpToken, setOtpToken] = useState('');
  // const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isInviteLoading, setIsInviteLoading] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  const resetAllErrors = () => {
    setEditErrors({});
  };

  useEffect(() => {
    resetAllErrors();
  }, []);

  useEffect(() => {
    if (loggedIn) {
      // if current_invite == accepted:
      //   navigate(AppUrls.DASHBOARD_URL, { replace: true });
      // else:
      console.log("Please logout to accept invite")
      addNotification('Please logout your current session to proceed', 'error');
      // ToDo: Show Dialog with Logout and Cancel(redirect to dashboard) buttons
    }
  }, [loggedIn]);

  const handleChange = (fieldAttr, value) => {
    // Clear error when user edits the field
    setEditErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    const form_data = new FormData(event.currentTarget);

    // Client-side check: passwords must match
    if (form_data.get('password') !== form_data.get('confirm_password')) {
      setEditErrors((prev) => ({
        ...prev,
        confirm_password: ["Passwords don't match"],
      }));
      return;
    }

    setIsInviteLoading(true); // Disable signup button
    resetAllErrors();

    const data = {
      event_type: "invite",
      profile_id: profile_id,
      invite_token: invite_token,
      invite_type: invite_type,
      // email: email,
      first_name: form_data.get('first_name'),
      last_name: form_data.get('last_name'),
      password: form_data.get('password'),
      // otp: form_data.get('otp'),
      terms_accepted: isTermsAccepted,
    };

    await httpRequest({
      method: 'POST',
      url: AUTH_URLS.EMAIL_LOGIN,
      data: data,
      onSuccess: (data) => {
        logger.log('Accept invite successful');
        login(data.access, data.refresh);
        addNotification('Invitation accepted, Welcome!!!', 'success');
        navigate(AppUrls.DASHBOARD_URL, { replace: true });
      },
      onFailure: (data) => {
        setEditErrors(data);
      },
      addNotification,
    });

    setIsInviteLoading(false);
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
            mt: 16,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <MaterialSymbol name="folded_hands" size={32} fill={1} />
          </Avatar>
          <Typography
            variant="h1"
            color="primary.main"
            sx={{ mb: 6 }}
          >
            Accept Invitation !!!
          </Typography>

          {/* Social Login */}
          <Grid container>
            <Grid item xs sx={{ textAlign: 'center', mt: 2 }}>
              <GoogleLoginButton event_type="invite"
                event_data={{
                  profile_id: profile_id,
                  invite_token: invite_token,
                  invite_type: invite_type,
                }}
              />
            </Grid>
            {/* <Grid item xs sx={{ textAlign: 'center', mt: 1 }}>
              <FacebookLoginButton event_type="invite" />
            </Grid> */}
          </Grid>

          {/* Divider with Or */}
          <Grid container>
            <Grid item xs>
              <Box sx={{ textAlign: 'center'}}>
                <Divider sx={{ mt: 4, mb: 1 }}>
                  <Typography variant="body1" color="text.secondary">
                    or
                  </Typography>
                </Divider>
              </Box>
              </Grid>
          </Grid>

          {/* Email/Password Based */}
          <Box
            component="form"
            noValidate
            onSubmit={handleInvite}
            sx={{ mt: 2 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="given-name"
                  name="first_name"
                  required
                  fullWidth
                  id="first_name"
                  label="First Name"
                  onChange={(e) =>
                    handleChange('first_name', e.target.value)
                  }
                  error={Boolean(editErrors.first_name)}
                  helperText={editErrors.first_name?.[0] || ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="last_name"
                  label="Last Name"
                  name="last_name"
                  autoComplete="family-name"
                  onChange={(e) =>
                    handleChange('last_name', e.target.value)
                  }
                  error={Boolean(editErrors.last_name)}
                  helperText={editErrors.last_name?.[0] || ''}
                />
              </Grid>
              <Grid item xs={12}>
              <Tooltip
                title={
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2">Your password must:</Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0.5 }}>
                      <Typography component="li" variant="body2">Be at least 12 characters long</Typography>
                      <Typography component="li" variant="body2">Include at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character (@$!%*?&)</Typography>
                      <Typography component="li" variant="body2">Not be a commonly used password</Typography>
                      <Typography component="li" variant="body2">Not be too similar to your personal information</Typography>
                    </Box>
                  </Box>
                }
                // placement="top"
                // arrow
              >
                  <TextField
                    required
                    fullWidth
                    name="password"
                    label="New Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    onChange={(e) => handleChange('password', e.target.value)}
                    error={Boolean(editErrors.password)}
                    helperText={editErrors.password?.[0] || ''}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="confirm_password"
                  label="Confirm Password"
                  type="password"
                  id="confirm_password"
                  autoComplete="confirm-new-password"
                  onChange={(e) =>
                    handleChange('confirm_password', e.target.value)
                  }
                  error={Boolean(editErrors.confirm_password)}
                  helperText={editErrors.confirm_password?.[0] || ''}
                />
              </Grid>
              {/* <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox value="allowExtraEmails" color="primary" />}
                  label="I want to receive inspiration, marketing promotions and updates via email."
                />
              </Grid> */}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={isTermsAccepted}
                      onChange={(e) => setIsTermsAccepted(e.target.checked)}
                    />
                  }
                  label={
                    <>
                      I agree to the{' '}
                      <Link
                        href="/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Tooltip
                  title={
                    loggedIn ? "Please logout your current session to continue" :
                    ( !isTermsAccepted ? "Please accept the terms & conditions to continue" : "" )
                  }
                  arrow
                  disableHoverListener={isTermsAccepted && !loggedIn} // hide tooltip if button is enabled
                >
                  <span>
                    <Button
                      type="submit"
                      // onClick={handleInvite}
                      fullWidth
                      variant="contained"
                      sx={{ mt: 3, mb: 2 }}
                      disabled={loggedIn || !isTermsAccepted || isInviteLoading}
                    >
                      Accept Invite
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Copyright sx={{ mt: 8 }} />
      </Container>
    </>
  );
}
